import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import getStyled from "../../hooks/getStyled";
import SFCard, { SFCardBody, SFCardFooter, SFCardHeader } from ".";
import { SFGridContainer, SFGridItem, SFStack } from "../grid";
import SFTypography from "../typography";
import SFButton from "../button";
import SFIconButton from "../button/iconButton";
import { SFDownloadIcon } from "../icons";

const { styled } = getStyled();

const SFCardStyled = styled(SFCard)(({ theme }) => ({
  backgroundColor: theme.palette.grey50.main,
  border: "none",
}));

const SFCardHeaderStyled = styled(SFCardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.grey50.main,
  paddingBottom: theme.spacing(1),
  borderBottom: "none !important",
  "& .MuiIconButton-root": {
    visibility: "hidden",
  },
  "&:hover": {
    backgroundColor: theme.palette.grey200.main,
    "& .MuiIconButton-root,": {
      visibility: "visible",
    },
  },
}));

const SFCardBodyStyled = styled(SFCardBody)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.grey50.main,
  padding: 0,
  paddingBottom: `${theme.spacing(1)} !important`,
}));

const SFCardFooterStyled = styled(SFCardFooter)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const SFIconButtonStyled = styled(SFIconButton)(() => ({
  padding: 0,
  backgroundColor: "transparent !important",
}));

const SFButtonStyled = styled(SFButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey50.main,
}));

const SFOverviewCard = ({
  header,
  count,
  subHeader,
  handleDownload,
  handleDetails,
  children,
}) => {
  const { t } = useTranslation();
  const exportRef = useRef(null);

  const downloadHandler = () => {
    handleDownload(exportRef);
  };

  const Header = () => {
    return (
      <SFGridContainer>
        <SFGridItem xs>
          <SFStack direction="column" spacing={0.5}>
            <SFTypography variant="cardTitle">{header}</SFTypography>
            <SFTypography variant="body2">
              <SFTypography variant="bold">
                {count + " "}
              </SFTypography>
              {subHeader}
            </SFTypography>
          </SFStack>
        </SFGridItem>
        <SFGridItem xs="auto">
          <SFIconButtonStyled variant="secondary" onClick={downloadHandler}>
            <SFDownloadIcon color="black" size="medium" />
          </SFIconButtonStyled>
        </SFGridItem>
      </SFGridContainer>
    );
  };

  return (
    <SFCardStyled>
      <div ref={exportRef}>
        <SFCardHeaderStyled subTitle={<Header />} />
        <SFCardBodyStyled>{children}</SFCardBodyStyled>
      </div>
      <SFCardFooterStyled>
        <SFButtonStyled variant="secondary" onClick={handleDetails} fullWidth>
          {t("components.overviewCard.detailsButton")}
        </SFButtonStyled>
      </SFCardFooterStyled>
    </SFCardStyled>
  );
};

SFOverviewCard.propTypes = {
  header: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  subHeader: PropTypes.string.isRequired,
  handleDownload: PropTypes.func.isRequired,
  handleDetails: PropTypes.func.isRequired,
};

export default SFOverviewCard;
